// Jemma capability registry — single source of truth for everything Jemma
// can do on Justin's behalf.
//
// =====================================================
// HOW TO ADD A NEW CAPABILITY
// =====================================================
//
// Two-step process. (1) Pick a stable ID and (2) call registerJemmaCapability
// at the top of the module that owns the feature. The Settings UI auto-renders
// a toggle per registered capability — no Settings-page edits needed.
//
// ID convention: '<area>.<verb>' lowercase. Areas already in use: brain,
// rfq, customer, quote, order, email. New areas welcome (graph, calendar,
// drawings, etc.) — keep names short.
//
//   // src/shared/calendarSomething.ts
//   import { registerJemmaCapability } from './jemmaCapabilities';
//   registerJemmaCapability({
//     id: 'calendar.read',
//     label: 'Read your calendar',
//     description: 'See your Outlook calendar to schedule callbacks and reminders.',
//     category: 'integration',
//     defaultGranted: false,
//     requires: 'Calendars.Read scope in Azure',
//   });
//
// Then at any call site that needs to gate on the user's choice:
//   if (await hasJemmaGrant('calendar.read')) { … }
//
// Categories are display-only — they bucket capabilities on the Settings
// page so the list isn't a wall of toggles. Add a new category by adding
// it to the union type below.
//
// defaultGranted is the "is this safe-by-default" knob. Reads and chat are
// on by default; anything that sends external comms, writes to integrations,
// or touches money should be defaultGranted: false and document `requires`
// so Justin knows the external setup step.

// =====================================================
// Types
// =====================================================

export type JemmaCapabilityCategory =
  | 'chat'
  | 'email'
  | 'memory'
  | 'data'
  | 'integration'
  | 'system';

export interface JemmaCapability {
  /** Stable ID. Convention: '<area>.<verb>'. Used in userSettings.jemmaGrants. */
  id: string;
  /** Short human label shown on the Settings toggle row. */
  label: string;
  /** One-line plain-English description of what this capability does. */
  description: string;
  /** Bucket the Settings UI groups by. */
  category: JemmaCapabilityCategory;
  /**
   * When true, capability is on by default — user must explicitly revoke.
   * When false, off by default — user must explicitly grant. Pick FALSE for
   * anything that sends comms, writes to integrations, or moves money.
   */
  defaultGranted: boolean;
  /**
   * Optional: external setup Justin needs to do for the capability to actually
   * work (e.g. "Requires Mail.Send scope in Azure"). Shown italic under the
   * toggle so granting + setup-needed reads as one thought.
   */
  requires?: string;
}

// =====================================================
// Registry
// =====================================================

const REGISTRY = new Map<string, JemmaCapability>();

/**
 * Register a Jemma capability. Idempotent — re-registering the same id
 * overwrites the existing entry (which is the desired behavior for HMR /
 * module re-execution in dev). The Settings UI re-reads on every render
 * so newly-registered capabilities show up without a reload.
 */
export function registerJemmaCapability(c: JemmaCapability): void {
  REGISTRY.set(c.id, c);
}

/**
 * All registered capabilities, in insertion order. Settings UI buckets these
 * by category — preserving registration order keeps related capabilities
 * (registered together at the top of the same module) visually adjacent.
 */
export function listJemmaCapabilities(): JemmaCapability[] {
  return Array.from(REGISTRY.values());
}

/** Look up a single capability. Useful for runtime gates that want to log
 *  "<label> not granted" rather than a bare id. */
export function getJemmaCapability(id: string): JemmaCapability | undefined {
  return REGISTRY.get(id);
}

/**
 * Runtime check: does the signed-in user have this capability granted?
 *
 * Resolution order:
 *   1. If userSettings.jemmaGrants[id] is explicitly set (true or false), use it.
 *   2. Otherwise fall back to capability.defaultGranted.
 *   3. If the capability isn't registered at all, return false (fail-closed
 *      — better to silently no-op than execute an action the user can't see
 *      in their Settings).
 *
 * Import-cycle note: this function imports from userSettings.ts which imports
 * COLLAR_DEFAULTS — there's no cycle back to here, but if a new userSettings
 * field ever needs to import from this file, we'd need to break it out.
 */
export async function hasJemmaGrant(id: string): Promise<boolean> {
  const cap = REGISTRY.get(id);
  if (!cap) return false;
  // Lazy import to keep the registry-only path (listJemmaCapabilities) free
  // of any Firebase dependency — important for tests that want to inspect
  // the registry without spinning up auth.
  const { getUserSettings } = await import('./userSettings');
  const s = await getUserSettings();
  const explicit = s.jemmaGrants?.[id];
  if (typeof explicit === 'boolean') return explicit;
  return cap.defaultGranted;
}

// =====================================================
// Seed — existing capabilities as of M1/M2
// =====================================================
//
// New capabilities should be registered at the top of the module that owns
// them, NOT added to this seed list. This block is just the initial
// kick-off for the registry so the Settings page has content from day one.

// Chat / brain
registerJemmaCapability({
  id: 'brain.chat',
  label: 'Chat with Jemma',
  description: 'Open the Jemma drawer and have a conversation.',
  category: 'chat',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'brain.recall',
  label: 'Remember facts about your business',
  description: 'Store and recall facts you tell her (customer preferences, shop rules, etc.).',
  category: 'memory',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'brain.remind',
  label: 'Set reminders',
  description: 'Create dated reminders that show up in your reminders panel.',
  category: 'memory',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'brain.flag',
  label: 'Flag issues for review',
  description: 'Surface things that need your attention in the header bell.',
  category: 'system',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'brain.watch',
  label: 'Watch business signals',
  description: 'Run background checks on quotes, orders, and customers — flag anomalies for review.',
  category: 'system',
  defaultGranted: true,
});

// Data — reads + writes against the CRM
registerJemmaCapability({
  id: 'rfq.extract',
  label: 'Extract structured data from RFQs',
  description: 'Parse RFQ text or PDFs into draft customers/parts/quotes for review.',
  category: 'data',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'customer.read',
  label: 'Read your customer rolodex',
  description: 'Look up customers, contacts, history when answering questions.',
  category: 'data',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'customer.write',
  label: 'Create and update customers',
  description: 'Add new customers and edit existing ones on your behalf.',
  category: 'data',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'quote.write',
  label: 'Create and update quotes',
  description: 'Build quotes from RFQs and edit existing quote records.',
  category: 'data',
  defaultGranted: true,
});
registerJemmaCapability({
  id: 'order.write',
  label: 'Create and update orders',
  description: 'Convert quotes to orders and update order status on your behalf.',
  category: 'data',
  defaultGranted: true,
});

registerJemmaCapability({
  id: 'material.read',
  label: 'Look up materials + vendors',
  description: 'Find which vendors supply a given material, so Jemma can route material RFQs.',
  category: 'data',
  defaultGranted: true,
});

// Email — gated by Microsoft Graph + Azure scopes
registerJemmaCapability({
  id: 'email.read',
  label: 'Read your Outlook inbox',
  description: 'Scan your inbox for RFQs, POs, and customer replies that need attention.',
  category: 'email',
  defaultGranted: false,
  requires: 'Mail.Read scope in Azure',
});
registerJemmaCapability({
  id: 'email.send_draft',
  label: 'Draft emails on your behalf',
  description: 'Compose replies and follow-ups as drafts in your Outlook — never auto-sent.',
  category: 'email',
  defaultGranted: false,
  requires: 'Mail.ReadWrite scope in Azure',
});
